import { Form, Formik } from 'formik';
import { isNil } from 'lodash';
import { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

// ASSETS
import { EditIcon } from 'assets';

// COMPONENTS
import { AddButton, PrimaryButton, SecondaryButton, SVGButton } from 'components/Buttons';
import FlashMessage from 'components/FlashMessage';
import Field, { ToggleInput, SelectField, PriceInputField, DisableableToggle } from 'components/FormField';
import { useModal } from 'components/Modals';
import { StyledCancelButton } from 'components/Modals/styles';

// CONSTANTS
import { AUTOFILLABLE_UNITS, CHARGE_RATE_UNITS, ChargeCategory, PER_UNIT } from 'constants/charges';
import { FLASH_MESSAGE_TYPES, flashMessageFactory, INITIAL_FLASH_MESSAGE } from 'constants/common';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// STYLES
import { BUTTON_INTENT } from 'styles/buttons';
import colors from 'styles/colors';
import { ModalContent, Modal as ModalDialog, ModalFooter, ModalHeader, Title } from 'styles/modals';
import { PriceCodeContainer, DirectiveContainer } from '../styles';
import { TaxesFieldset } from './styles';

// SERVICES
import { addCharge, removeCharge, editCharge } from 'services/FleetSettings/thunks';
// TODO: extract from Usermanagement and import from new generic location
import { selectDealershipId } from 'services/Authentication/selectors';
import { selectTaxes } from 'services/FleetSettings/selectors';

// TYPES
import { DefaultChargeCreation, DefaultCharge } from 'services/resources';

// UTILS
import { roundToDecimal } from 'utils/number.utils';

type ChargeButtonProps = React.ComponentPropsWithoutRef<typeof PrimaryButton> & {
  warAndDisc?: boolean;
} & (
    | {
        edit?: boolean;
        charge?: DefaultCharge;
      }
    | {
        edit: true;
        charge: DefaultCharge;
      }
  );

export const DISCOUNTS_VALUES = [ChargeCategory.DISCOUNT, ChargeCategory.WARRANTY];

type FormChargeType = Omit<DefaultChargeCreation, 'taxes'> & {
  taxes?: string[];
};

const useChargeForm = (
  charge: DefaultCharge | null,
  {
    isDiscount = false,
    isEdit = false,
    setFlashMessage,
  }: { isDiscount?: boolean; isEdit?: boolean; setFlashMessage: (flashMessage: typeof INITIAL_FLASH_MESSAGE) => void }
) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const taxes = useSelector(selectTaxes());

  const dealershipId = useSelector(selectDealershipId());

  const categoryOptions = useMemo(() => {
    const filteredCategories = Object.values(ChargeCategory).filter((category) => {
      // If warAndDisc is true, only include special categories
      // Otherwise, include all categories except the special ones
      return isDiscount ? DISCOUNTS_VALUES.includes(category) : !DISCOUNTS_VALUES.includes(category);
    });

    const options = filteredCategories.map((category) => {
      return {
        label: formatMessage({
          id: `views.FleetSettings.Charges.categories.${category}`,
        }),
        value: category,
      };
    });
    return options;
  }, [formatMessage, isDiscount]);

  const rateOptions = useMemo(() => {
    const options = Object.values(CHARGE_RATE_UNITS).map((rate) => {
      return {
        label: formatMessage({
          id: rate.label.translationId,
        }),
        value: rate.value,
      };
    });
    return options;
  }, [formatMessage]);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        autofill: Yup.boolean(),
        category: Yup.string<ChargeCategory>().max(20).oneOf(Object.values(ChargeCategory)),
        code: Yup.string()
          .required(formatMessage({ id: 'validators.yup.mixed.required' }, { path: 'Code' }))
          .max(50),
        description: Yup.string()
          .required(formatMessage({ id: 'validators.yup.mixed.required' }, { path: 'Title' }))
          .max(100),
        isDefaultCharge: Yup.boolean(),
        name: Yup.string().nullable().max(100),
        overridable: Yup.boolean(),
        price: Yup.number(),
        quantityUnit: Yup.string().nullable().max(14),
        taxes: Yup.array().nullable().of(Yup.number()),
      }),
    [formatMessage]
  );

  const getSanitizedCharge = (newCharge: FormChargeType): DefaultChargeCreation => {
    const castCharge = schema.cast(newCharge);

    const { description, code, price } = castCharge;

    const roundedPrice = roundToDecimal(price, 2);

    const sanitizedCharge = {
      autofill: newCharge.autofill,
      description,
      code,
      category: newCharge.category,
      price: roundedPrice,
      overridable: newCharge.overridable,
      isDefaultCharge: newCharge.isDefaultCharge,
      quantityUnit: isNil(newCharge.quantityUnit) ? null : newCharge.quantityUnit,
      dealership: isEdit && charge ? charge.dealership : dealershipId,
      isSelected: true,
      name: '',
      taxes: newCharge.taxes?.map((taxId) => taxes.find((tax) => tax.get('id').toString() === taxId).toJS()),
    };

    return sanitizedCharge;
  };

  const onSubmit = async (values: FormChargeType) => {
    setSubmitLoading(true);
    const sanitizedCharge = getSanitizedCharge(values);

    const flashMessage = flashMessageFactory({
      shown: true,
      translationId: isEdit
        ? 'views.FleetSettings.Charges.flashMessage.editSuccess'
        : 'views.FleetSettings.Charges.flashMessage.creationSuccess',
    });

    try {
      if (isEdit) {
        await dispatch(editCharge(charge.id, sanitizedCharge, charge?.dealership ?? null));
      } else {
        await dispatch(addCharge(sanitizedCharge));
      }
      setFlashMessage(flashMessage);
    } catch (error) {
      const flashMessage = flashMessageFactory({
        shown: true,
        type: FLASH_MESSAGE_TYPES.error,
        translationId: 'views.FleetSettings.Charges.flashMessage.creationFailed',
      });
      setFlashMessage(flashMessage);
    }
    setSubmitLoading(false);
  };

  const onDelete = async () => {
    try {
      setDeleteLoading(true);
      await dispatch(removeCharge(charge.id, charge?.dealership ?? null));
    } catch (err) {
      const flashMessage = flashMessageFactory({
        shown: true,
        type: FLASH_MESSAGE_TYPES.error,
        translationId: 'views.FleetSettings.Charges.flashMessage.deleteFailed',
      });
      setFlashMessage(flashMessage);
    }
  };

  let initialValues: FormChargeType = {
    autofill: false,
    category: isDiscount ? ChargeCategory.DISCOUNT : ChargeCategory.OTHER,
    code: '',
    description: '',
    isDefaultCharge: false,
    name: '',
    overridable: false,
    price: 0,
    quantityUnit: PER_UNIT.notApplicable,
    dealership: null,
  };

  if (charge) {
    initialValues = {
      autofill: charge.autofill,
      category: charge.category,
      code: charge.code,
      description: charge.description,
      isDefaultCharge: charge.isDefaultCharge,
      name: charge.name,
      overridable: charge.overridable,
      price: charge.price,
      quantityUnit: charge.quantityUnit,
      taxes: charge.taxes.map((tax) => tax.id.toString()) || [],
      dealership: null,
    };
  } else {
    initialValues.taxes = [];
  }

  return {
    categoryOptions,
    deleteLoading,
    initialValues,
    onDelete,
    onSubmit,
    rateOptions,
    schema,
    submitLoading,
  };
};

export const ChargeForm = ({
  closeModal,
  charge,
  isDiscount = false,
  isEdit = false,
  setFlashMessage,
}: {
  closeModal: (event?: React.SyntheticEvent) => void;
  charge: DefaultCharge;
  isDiscount?: boolean;
  isEdit?: boolean;
  setFlashMessage: (flashMessage: typeof INITIAL_FLASH_MESSAGE) => void;
}) => {
  const { formatMessage } = useIntl();
  const { categoryOptions, deleteLoading, initialValues, onDelete, onSubmit, rateOptions, schema, submitLoading } =
    useChargeForm(charge, {
      isDiscount,
      isEdit,
      setFlashMessage,
    });

  const taxes = useSelector(selectTaxes());

  const renderActionTitle = () => {
    if (isEdit && !isDiscount) {
      return <FormattedMessage id="views.FleetSettings.Charges.actions.editCharge" />;
    } else if (isDiscount && isEdit) {
      return <FormattedMessage id="views.FleetSettings.Charges.actions.editDiscount" />;
    } else if (isDiscount && !isEdit) {
      return <FormattedMessage id="views.FleetSettings.Charges.actions.addDiscount" />;
    } else {
      return <FormattedMessage id="views.FleetSettings.Charges.actions.addCharge" />;
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values) => {
        await onSubmit(values);
        closeModal();
      }}
    >
      {(formik) => {
        const autofillDisabled = !AUTOFILLABLE_UNITS.includes(formik.values.quantityUnit);

        return (
          <>
            <ModalHeader>
              <Title>{renderActionTitle()}</Title>
              {isEdit && (
                <SecondaryButton intent={BUTTON_INTENT.danger} loading={deleteLoading} onClick={onDelete}>
                  <FormattedMessage id="buttons.delete" />
                </SecondaryButton>
              )}
            </ModalHeader>
            <Form>
              <ModalContent>
                <div>
                  {!isEdit && (
                    <DirectiveContainer>
                      <FormattedMessage
                        id={
                          isDiscount
                            ? 'views.FleetSettings.Charges.warDiscDirective'
                            : 'views.FleetSettings.Charges.chargeDirective'
                        }
                      />
                    </DirectiveContainer>
                  )}
                  <Field name="description" label={formatMessage({ id: 'views.FleetSettings.Charges.item' })} />
                </div>

                <PriceCodeContainer>
                  <Field
                    as={PriceInputField}
                    name="price"
                    label={formatMessage({ id: 'views.FleetSettings.Charges.price' })}
                    ensurePositive
                  />
                  <Field name="code" label={formatMessage({ id: 'views.FleetSettings.Charges.code' })} />
                </PriceCodeContainer>

                <Field
                  as={ToggleInput}
                  type="checkbox"
                  name="overridable"
                  inlineLabel={formatMessage({ id: 'views.FleetSettings.Charges.actions.allowOverride' })}
                  description={formatMessage({
                    id: 'views.FleetSettings.Charges.actions.allowOverrideDescription',
                  })}
                  tooltipActive
                />
                <Field
                  as={SelectField}
                  name="category"
                  label={formatMessage({ id: 'views.FleetSettings.Charges.category' })}
                  options={categoryOptions}
                />
                {!isDiscount && (
                  <Field
                    as={SelectField}
                    name="quantityUnit"
                    label={formatMessage({ id: 'views.FleetSettings.Charges.unit' })}
                    options={rateOptions}
                  />
                )}
                {!isDiscount && (
                  <Field
                    as={DisableableToggle}
                    dependencyName="quantityUnit"
                    shouldUncheck={(value) => !AUTOFILLABLE_UNITS.includes(value)}
                    name="autofill"
                    type="checkbox"
                    disabled={autofillDisabled}
                    title={
                      autofillDisabled
                        ? formatMessage({ id: 'views.FleetSettings.Charges.autofillDisabled' })
                        : undefined
                    }
                    inlineLabel={formatMessage({ id: 'views.FleetSettings.Charges.autofill' })}
                    description={formatMessage({
                      id: 'views.FleetSettings.Charges.autofillDescription',
                    })}
                    tooltipActive
                  />
                )}
                {!isDiscount && (
                  <TaxesFieldset>
                    <legend>
                      <FormattedMessage id="views.FleetSettings.Taxes.title" />
                    </legend>
                    {taxes
                      .map((tax) => (
                        <Field
                          key={tax.get('id')}
                          as={ToggleInput}
                          type="checkbox"
                          name="taxes"
                          inlineLabel={`${tax.get('code')} (${tax.get('rate')}%)`}
                          value={tax.get('id').toString()}
                        />
                      ))
                      .toArray()}
                  </TaxesFieldset>
                )}
                <Field
                  as={ToggleInput}
                  type="checkbox"
                  name="isDefaultCharge"
                  inlineLabel={formatMessage({
                    id: 'views.FleetSettings.Charges.actions.addToAgreementsByDefault',
                  })}
                />
              </ModalContent>
              <ModalFooter>
                <StyledCancelButton onClick={closeModal} />
                <PrimaryButton
                  intent={isEdit ? BUTTON_INTENT.success : BUTTON_INTENT.primary}
                  type="submit"
                  loading={submitLoading}
                  disabled={!formik.isValid || !formik.dirty}
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {isEdit && isDiscount ? (
                    <FormattedMessage id="views.FleetSettings.Charges.actions.updateDiscount" />
                  ) : isEdit ? (
                    <FormattedMessage id="views.FleetSettings.Charges.actions.updateCharge" />
                  ) : (
                    <FormattedMessage id="views.FleetSettings.Charges.actions.createCharge" />
                  )}
                </PrimaryButton>
              </ModalFooter>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};

const ChargeButton = ({ warAndDisc = false, charge, edit = false, ...rest }: ChargeButtonProps) => {
  const { formatMessage } = useIntl();
  const [flashMessageState, setFlashMessageState] = useState(INITIAL_FLASH_MESSAGE);

  const { openModal, isOpen, Modal, closeModal } = useModal({
    background: colors.modalBackground,
  });

  const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    openModal(event);
  };

  return (
    <>
      {/* eslint-disable-next-line no-nested-ternary */}
      {edit ? (
        <SVGButton {...rest} className="fleet-charges-edit-charge" onClick={onClick} color={colors.fleetBlue}>
          <EditIcon color={colors.fleetBlue} />
        </SVGButton>
      ) : warAndDisc ? (
        <AddButton {...rest} onClick={onClick}>
          <FormattedMessage id="views.FleetSettings.Charges.actions.addDiscount" />
        </AddButton>
      ) : (
        <AddButton {...rest} onClick={onClick}>
          <FormattedMessage id="views.FleetSettings.Charges.actions.addCharge" />
        </AddButton>
      )}
      {flashMessageState.shown && (
        <FlashMessage
          type={flashMessageState.type}
          text={formatMessage({ id: flashMessageState.translationId }, flashMessageState.translationValueObject)}
          onClose={() => {
            setFlashMessageState(INITIAL_FLASH_MESSAGE);
          }}
          visible
        />
      )}
      {isOpen && (
        <Modal>
          <ModalDialog>
            <ChargeForm
              charge={charge}
              isEdit={edit}
              isDiscount={warAndDisc}
              setFlashMessage={setFlashMessageState}
              closeModal={closeModal}
            />
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default { ChargeButton };
